// screens/PersonSpread.tsx — B17c: one person, whole. The party row plus
// everything the server DERIVES about them: journey state (contact → lead
// → customer), primary email, and per-identity contactability — the
// may-we-message-now answer with the server's own reason. No stored flag
// is rendered here and none exists: the client shows the answer, never
// re-derives it (an implied-consent window closes with nothing about the
// row changing — a cached boolean goes stale exactly when it matters).
//
// Park/unpark in place: parked is a real state worn openly, not a
// deletion. Touches read newest-first; recording one is files-are-approval.
// A claimed identity's takeover is NEVER swallowed (§2.1): if the address
// changed hands, this screen says so where it happened.
//
// History (G6 C2, dab35ce1): every conversation that belongs to this person, newest first, across
// doors — email, calls, texts, DMs. The SERVER decides membership (GET /api/parties/:id/history,
// thread/partyHistory.ts): a person's confirmation, or a message from an address or number on file.
// The client never re-derives it. A confirmed row says so in words.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  claimPartyIdentity,
  getParty,
  getPartyContactability,
  draftLetterForParty,
  getPartyHistory,
  listPartyTouches,
  startClientForParty,
  mintTelegramLink,
  recordPartyTouch,
  setPartyParked,
  type ContactabilityApi,
  type PartyDetailApi,
  type PartyHistoryItemApi,
  type PartyTouchApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import './PersonSpread.css';

export interface PersonSpreadProps {
  tenantId: string;
  id: string;
  backLabel?: string;
  onBack: () => void;
  /** Opens one conversation from the history. Absent = the rows are read-only. */
  onOpenThread?: (threadId: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; detail: PartyDetailApi };

const IDENTITY_KINDS = ['email', 'phone', 'whatsapp', 'telegram', 'other'] as const;

/** G6: the history row's channel in the words a person uses, not the door's code. */
const CHANNEL_WORD: Record<string, string> = {
  email: 'email', call: 'call', voice: 'call', social: 'DM', whatsapp: 'WhatsApp', telegram: 'Telegram', sms: 'text', portal: 'portal',
};

function touchLabel(iso: string): string {
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PersonSpread({ tenantId, id, backLabel, onBack, onOpenThread }: PersonSpreadProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [touches, setTouches] = useState<PartyTouchApi[]>([]);
  const [history, setHistory] = useState<PartyHistoryItemApi[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [contactability, setContactability] = useState<Map<string, ContactabilityApi>>(new Map());
  const [identityKind, setIdentityKind] = useState<string>('email');
  const [identityValue, setIdentityValue] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // B17e — the link, held only until the screen is left. Shown once, because
  // a capability that lingers on a screen is a capability lying around.
  const [tgLink, setTgLink] = useState<string | null>(null);
  // B18f — starting somebody Huan already knows. The accept link is a
  // capability, held only until this screen is left.
  const [startEmail, setStartEmail] = useState('');
  const [starting, setStarting] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);

  function refresh() {
    return getParty(tenantId, id)
      .then(async (detail) => {
        setLoad({ phase: 'ready', detail });
        // One contactability read per kind the person actually has — the
        // door answers per kind, and the answer is keyed back to each
        // identity row. A failed read costs the word, never the identity.
        const liveKinds = [...new Set(detail.identities.filter((i) => !i.releasedAt).map((i) => i.kind))];
        const answers = await Promise.all(
          liveKinds.map((k) => getPartyContactability(tenantId, id, k).catch(() => [] as ContactabilityApi[]))
        );
        setContactability(new Map(answers.flat().map((c) => [c.identityId, c])));
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setLoad({ phase: 'error', message: 'person not found' });
        } else {
          setLoad({
            phase: 'error',
            message: err instanceof ApiError ? err.message : 'this person could not be read',
          });
        }
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    void listPartyTouches(tenantId, id).then(setTouches).catch(() => { /* renders without */ });
    setHistory(null);
    setHistoryError(null);
    void getPartyHistory(tenantId, id)
      .then(setHistory)
      .catch((err: unknown) => setHistoryError(err instanceof ApiError ? err.message : 'the history could not be read'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  async function togglePark(parked: boolean) {
    setNote(null);
    setBusy('park');
    try {
      await setPartyParked(tenantId, id, parked);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the change was not recorded');
    } finally {
      setBusy(null);
    }
  }

  /** B17e — "connect their Telegram". Mints the deep link; sending it is
   *  Huan's act, in whatever conversation he is already having with them. */
  async function connectTelegram() {
    setNote(null);
    setBusy('telegram');
    try {
      const { link } = await mintTelegramLink(tenantId, id);
      setTgLink(link);
    } catch (err) {
      setNote(
        err instanceof ApiError && err.code === 'telegram_not_configured'
          ? 'This instance has no bot to connect them to yet.'
          : err instanceof ApiError ? err.message : 'no link was made'
      );
    } finally {
      setBusy(null);
    }
  }

  /** B18f — make them a client. Until this door existed the lifecycle was
   *  reachable only by strangers who filled in the funnel form. */
  async function startAsClient() {
    const email = startEmail.trim();
    if (!email) return;
    setNote(null);
    setBusy('start');
    try {
      const r = await startClientForParty(tenantId, id, { email });
      setAcceptUrl(r.acceptUrl);
      setStarting(false);
      setStartEmail('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'they were not started');
    } finally {
      setBusy(null);
    }
  }

  /** B18f — write this client a letter now. The cadence is a rhythm, not a
   *  rule, so a person asking for one outranks it; everything it produces
   *  still waits in Review. */
  async function writeLetter() {
    setNote(null);
    setBusy('letter');
    try {
      const r = await draftLetterForParty(tenantId, id);
      setNote(
        r.outcome === 'drafted' ? 'Drafted — it is in Review for your say-so.'
        : r.outcome === 'nothing_queued' ? 'Nothing is queued for them yet.'
        : r.outcome === 'already_in_flight' ? 'One is already waiting — settle that first.'
        : r.outcome === 'no_engagement' ? 'They are not a client yet, so there is nothing to propose.'
        : 'Not due yet — open one anyway from their engagement if you need to.'
      );
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the letter was not written');
    } finally {
      setBusy(null);
    }
  }

  async function addIdentity() {
    const value = identityValue.trim();
    if (!value) return;
    setNote(null);
    setBusy('identity');
    try {
      const claimed = await claimPartyIdentity(tenantId, id, { kind: identityKind, value });
      if (claimed.displaced) {
        setNote('That address changed hands — the previous holder shows under People.');
      } else if (claimed.unchanged) {
        setNote('Already theirs.');
      }
      setIdentityValue('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not added');
    } finally {
      setBusy(null);
    }
  }

  async function addTouch() {
    setNote(null);
    setBusy('touch');
    try {
      await recordPartyTouch(tenantId, id, {
        occurredAt: new Date().toISOString(),
        attribution: { arrivalDoor: 'manual' },
      });
      const rows = await listPartyTouches(tenantId, id);
      setTouches(rows);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the touch was not recorded');
    } finally {
      setBusy(null);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="pspread">
        <BackLink label={backLabel ?? 'People'} onClick={onBack} />
        <div class="pspread__skeleton" aria-hidden="true">
          <div class="pspread__skeleton-block" />
          <div class="pspread__skeleton-block" />
        </div>
      </div>
    );
  }

  if (load.phase === 'error') {
    return (
      <div class="pspread">
        <BackLink label={backLabel ?? 'People'} onClick={onBack} />
        <div class="pspread__say">
          This person could not be read.
          <br />
          <span class="pspread__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { party, identities, journeyState, primaryEmail } = load.detail;
  const parked = party.parkedAt !== null;
  const live = identities.filter((i) => !i.releasedAt);
  const released = identities.filter((i) => i.releasedAt);
  const orderedTouches = [...touches].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

  return (
    <div class="pspread">
      <BackLink label={backLabel ?? 'People'} onClick={onBack} />

      <header class="pspread__head">
        <div class="pspread__journey">{journeyState}</div>
        <h1 class="pspread__name">
          {party.displayName}
          {party.kind === 'organization' ? <span class="pspread__kind">org</span> : null}
        </h1>
        {primaryEmail ? <div class="pspread__email">{primaryEmail}</div> : null}
        {parked ? <div class="pspread__parked-word">parked</div> : null}
      </header>

      <div class="pspread__actions">
        <button type="button" class="pspread__act" disabled={busy === 'start'} onClick={() => setStarting(true)}>
          Start them as a client
        </button>
        <button type="button" class="pspread__act" disabled={busy === 'letter'} onClick={() => void writeLetter()}>
          Write them a letter
        </button>
        <button type="button" class="pspread__act" disabled={busy === 'telegram'} onClick={() => void connectTelegram()}>
          Connect their Telegram
        </button>
        <button type="button" class="pspread__act" disabled={busy === 'park'} onClick={() => void togglePark(!parked)}>
          {parked ? 'Unpark' : 'Park'}
        </button>
      </div>
      {note ? <div class="pspread__note">{note}</div> : null}
      {starting ? (
        <div class="pspread__tglink">
          <input
            class="pspread__start-email"
            type="email"
            placeholder="Where should the invitation go?"
            value={startEmail}
            disabled={busy === 'start'}
            onInput={(e) => setStartEmail((e.target as HTMLInputElement).value)}
          />
          <div class="pspread__start-actions">
            <button type="button" class="pspread__act" disabled={busy === 'start' || !startEmail.trim()}
              onClick={() => void startAsClient()}>
              Start them
            </button>
            <button type="button" class="pspread__act" disabled={busy === 'start'}
              onClick={() => { setStarting(false); setStartEmail(''); }}>
              Never mind
            </button>
          </div>
        </div>
      ) : null}
      {acceptUrl ? (
        <div class="pspread__tglink">
          <div class="pspread__tglink-value">{acceptUrl}</div>
          <div class="pspread__tglink-note">
            Their invitation. It has also been emailed to them; this is here in case
            you would rather send it yourself.
          </div>
        </div>
      ) : null}
      {tgLink ? (
        <div class="pspread__tglink">
          {/* Selectable, not a link to follow: the person who should open it
              is THEM, so this is here to be copied into a message. */}
          <div class="pspread__tglink-value">{tgLink}</div>
          <div class="pspread__tglink-note">
            Send this to them. Opening it connects their Telegram to this person, and
            whatever they write lands here.
          </div>
        </div>
      ) : null}

      <section class="pspread__group">
        <h2 class="pspread__group-title">Identities</h2>
        {live.length === 0 ? (
          <div class="pspread__quiet">No way to reach them yet.</div>
        ) : (
          live.map((i) => {
            const c = contactability.get(i.id);
            return (
              <div class="pspread__identity" key={i.id}>
                <span class="pspread__identity-main">
                  <span class="pspread__identity-value">{i.value}</span>
                  <span class="pspread__identity-kind">{i.kind}</span>
                </span>
                {c ? (
                  // The server's word, verbatim: contactable, or its reason
                  // why not. Never colour alone — the word carries it.
                  <span class={`pspread__contactable${c.contactable ? '' : ' is-not'}`}>
                    {c.contactable ? 'contactable' : c.reason}
                  </span>
                ) : null}
              </div>
            );
          })
        )}
        {released.length > 0 ? (
          <div class="pspread__released">
            {released.map((i) => (
              <div class="pspread__released-line" key={i.id}>
                {i.value} · released{i.releasedReason ? ` — ${i.releasedReason}` : ''}
              </div>
            ))}
          </div>
        ) : null}
        <div class="pspread__add">
          <select
            class="pspread__add-kind"
            value={identityKind}
            disabled={busy === 'identity'}
            onChange={(e) => setIdentityKind((e.target as HTMLSelectElement).value)}
          >
            {IDENTITY_KINDS.map((k) => <option value={k} key={k}>{k}</option>)}
          </select>
          <input
            class="pspread__add-input"
            type="text"
            placeholder="Add an address…"
            value={identityValue}
            disabled={busy === 'identity'}
            onInput={(e) => setIdentityValue((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void addIdentity(); }}
          />
          <button type="button" class="pspread__act" disabled={busy === 'identity' || !identityValue.trim()} onClick={() => void addIdentity()}>
            Add
          </button>
        </div>
      </section>

      <section class="pspread__group">
        <h2 class="pspread__group-title">History</h2>
        {historyError ? (
          <div class="pspread__quiet" role="alert">{historyError}</div>
        ) : history === null ? null : history.length === 0 ? (
          <div class="pspread__quiet">No conversations yet.</div>
        ) : (
          history.map((h) => {
            const row = (
              <>
                <span class="pspread__touch-when">{h.lastMessageAt ? touchLabel(h.lastMessageAt) : '—'}</span>
                {h.channel ? <span class="pspread__touch-door">{CHANNEL_WORD[h.channel] ?? h.channel}</span> : null}
                <span class="pspread__history-subject">{h.subject ?? '(no subject)'}</span>
                {h.route === 'confirmed' ? <span class="pspread__touch-door">confirmed</span> : null}
              </>
            );
            return onOpenThread ? (
              <button type="button" class="pspread__touch pspread__history" key={h.threadId} onClick={() => onOpenThread(h.threadId)}>
                {row}
              </button>
            ) : (
              <div class="pspread__touch pspread__history" key={h.threadId}>{row}</div>
            );
          })
        )}
      </section>

      <section class="pspread__group">
        <h2 class="pspread__group-title">Touches</h2>
        {orderedTouches.length === 0 ? (
          <div class="pspread__quiet">No touches recorded.</div>
        ) : (
          orderedTouches.map((t) => (
            <div class="pspread__touch" key={t.id}>
              <span class="pspread__touch-when">{touchLabel(t.occurredAt)}</span>
              {t.arrivalDoor ? <span class="pspread__touch-door">{t.arrivalDoor}</span> : null}
            </div>
          ))
        )}
        <button type="button" class="pspread__act pspread__act--touch" disabled={busy === 'touch'} onClick={() => void addTouch()}>
          Record a touch
        </button>
      </section>
    </div>
  );
}
